import { AxiosRequestConfig } from "axios"
import cloneDeep from "lodash/cloneDeep"
import { useMutation, useQueryClient } from "react-query"

import { arrayWrap, CamelToSnakeCaseNested, pythonify } from "@ncs/ts-utils"

import { apiClient, ecommApiClient } from "../util"
import { ApiPostMutation, ApiPostMutationOptions } from "./types"

export const usePutRequest = <RequestData = void, ResponseData = unknown>(
	endpoint: string | string[],
	options?: ApiPostMutationOptions<RequestData, ResponseData>,
	useEcommService?: boolean
): ApiPostMutation<RequestData, ResponseData> => {
	const queryClient = useQueryClient()

	const tryToAddId = (requestData: RequestData): string => {
		const id = requestData["id" as keyof RequestData] ?? ""

		if (id) {
			return `${id}/`
		}

		return ""
	}

	const tryToDeleteId = (requestData: RequestData): RequestData | Omit<RequestData, "id"> => {
		const id = requestData["id" as keyof RequestData] ?? ""

		if (id) {
			const putData = cloneDeep(requestData)
			delete putData["id" as keyof RequestData]

			return putData
		}

		return requestData
	}

	const mutation = useMutation(
		(requestDataRaw: RequestData) => {
			const requestData =
				options?.addRequestDataIdToUrl ? tryToDeleteId(requestDataRaw) : requestDataRaw

			let axiosConfig: AxiosRequestConfig | undefined
			if (requestData instanceof FormData) {
				axiosConfig = {
					headers: {
						"content-type":
							"multipart/form-data; boundary=----WebKitFormBoundary6CQcHKAPzXlSYnJt",
					},
				}
			}

			const client = useEcommService ? ecommApiClient : apiClient

			return client.put<CamelToSnakeCaseNested<ResponseData>>(
				`${arrayWrap(endpoint).join("/")}/${
					options?.addRequestDataIdToUrl ? tryToAddId(requestDataRaw) : ""
				}${
					options?.urlPathsAfterId ?
						`${arrayWrap(options.urlPathsAfterId).join("/")}/`
					:	""
				}`,
				requestData instanceof FormData ? requestData : (
					pythonify(requestData, options?.pythonifyExcludeList)
				),
				axiosConfig
			)
		},
		{
			onError: (e) => {
				if (options?.onError) {
					options.onError(e)
				}
				console.error("usePutRequest mutation error", e)
			},
			onSuccess: (data, requestData) => {
				if (options?.onSuccess) {
					options.onSuccess(data, requestData)
				}
				if (options?.keyToInvalidate) {
					const keys =
						typeof options.keyToInvalidate === "string" ?
							[options.keyToInvalidate]
						:	options.keyToInvalidate
					keys.forEach((key) => {
						void queryClient.invalidateQueries(key)
					})
				}
			},
			retry: false,
		}
	)

	return mutation.mutateAsync
}
